import { useState, useEffect } from 'react';
import {
  MeDto,
  AppSettingsContentResponseModel,
  PersonalInformationScreenPropertiesModel,
  SalutationContentResponseModel,
  UpdateProfileRequest,
} from '@/services/platform/api/model';
import { getProfileSalutations } from '@/services/platform/data/profile';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useClientTranslation } from '@/lib/core';
import { useLocaleStore } from '@/lib/core/stores';

export interface EditProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: MeDto | undefined;
  pageProperties: PersonalInformationScreenPropertiesModel | undefined;
  appSettings: AppSettingsContentResponseModel | undefined;
  onSave: (data: UpdateProfileRequest) => Promise<void>;
}

export function EditProfileSheet({
  open,
  onOpenChange,
  profile,
  pageProperties,
  appSettings,
  onSave,
}: EditProfileSheetProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const [saving, setSaving] = useState(false);
  const [salutation, setSalutation] = useState(profile?.salutation ?? '');
  const [firstName, setFirstName] = useState(profile?.firstName ?? '');
  const [lastName, setLastName] = useState(profile?.lastName ?? '');
  const [salutations, setSalutations] = useState<SalutationContentResponseModel[]>([]);

  useEffect(() => {
    if (!open) return;
    setSalutation(profile?.salutation ?? '');
    setFirstName(profile?.firstName ?? '');
    setLastName(profile?.lastName ?? '');
  }, [open, profile]);

  useEffect(() => {
    if (!open || !appSettings) return;
    getProfileSalutations(appSettings, locale).then(items => {
      if (items) setSalutations(items);
    });
  }, [open, appSettings, locale]);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({ salutation, firstName, lastName });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-4">
          <div className="size-11" />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] uppercase">
            {pageProperties?.editProfileTitle ?? t('App.Labels.EditProfile')}
          </p>
          <div className="size-11" />
        </div>

        {/* Form */}
        <div className="flex flex-col gap-4 px-4 py-6">
          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-salutation" className="font-inter">
              {t('App.Labels.Title')}
            </Label>
            <Select value={salutation} onValueChange={setSalutation}>
              <SelectTrigger id="edit-salutation" className="font-normal normal-case">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent withoutPortal>
                {salutations.map(item => {
                  const label = item.properties?.salutationName ?? item.name ?? '';
                  return (
                    <SelectItem key={item.id} value={label}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-first-name" className="font-inter">
              {t('App.Labels.FirstName')}
            </Label>
            <Input id="edit-first-name" value={firstName} onChange={e => setFirstName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="edit-last-name" className="font-inter">
              {t('App.Labels.LastName')}
            </Label>
            <Input id="edit-last-name" value={lastName} onChange={e => setLastName(e.target.value)} />
          </div>
        </div>

        {/* Footer */}
        <DrawerFooter>
          <Button variant="primary-solid" palette="dark" size="md" loading={saving} onClick={handleSave}>
            {pageProperties?.editProfileSaveChangesBtn ?? t('App.Labels.SaveChanges')}
          </Button>
          <DrawerClose
            render={
              <Button variant="tertiary-link" palette="dark" size="sm">
                {pageProperties?.editProfileCancelBtn ?? t('App.Labels.Cancel')}
              </Button>
            }
          />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
